/**
 * Goals Service
 * Manages training goals and progress tracking
 */

import { ref, onValue, off } from 'firebase/database';
import { database } from './firebase/core';
import { getData, setData, updateData, removeData } from './firebase/api-wrapper.service';
import { Goal as SimpleGoal, GoalProgress, GoalStats, PublicGoal, GoalType, GoalFrequency } from '../types/achievements';
import goalNotificationsService from './goalNotifications.service';
import logger from '../utils/logger';

// Use the simpler Goal type from achievements for compatibility
type Goal = SimpleGoal;

class GoalsService {
  private publicGoalsCache: Map<string, PublicGoal> = new Map();

  /**
   * Get current period string
   */
  private getCurrentPeriod(frequency: GoalFrequency): string {
    const now = new Date();
    const year = now.getFullYear();
    
    if (frequency === 'weekly') {
      // Get ISO week number
      const onejan = new Date(year, 0, 1);
      const week = Math.ceil((((now.getTime() - onejan.getTime()) / 86400000) + onejan.getDay() + 1) / 7);
      return `${year}-${week}`;
    } else {
      // Monthly
      const month = now.getMonth() + 1;
      return `${year}-${month}`;
    }
  }

  /**
   * Get public goal templates
   */
  async getPublicGoals(): Promise<PublicGoal[]> {
    try {
      const goalsData = await getData('goals/public');
      if (goalsData) {
        const goals = Object.entries(goalsData).map(([id, data]) => ({
          id,
          ...(data as Omit<PublicGoal, 'id'>),
        }));
        
        // Update cache
        goals.forEach(goal => this.publicGoalsCache.set(goal.id, goal));
        
        return goals;
      }
      return [];
    } catch (error) {
      logger.error('Error fetching public goals:', error);
      return [];
    }
  }

  /**
   * Get user's private goals
   */
  async getUserGoals(uid: string): Promise<Goal[]> {
    try {
      const goalsData = await getData(`goals/private/${uid}`);
      if (goalsData) {
        return Object.entries(goalsData).map(([id, data]) => ({
          id,
          ...(data as Omit<Goal, 'id'>),
        }));
      }
      return [];
    } catch (error) {
      logger.error('Error fetching user goals:', error);
      return [];
    }
  }

  /**
   * Get specific goal
   */
  async getUserGoal(uid: string, goalId: string): Promise<Goal | null> {
    try {
      const goalData = await getData(`goals/private/${uid}/${goalId}`);
      if (goalData) {
        return { id: goalId, ...goalData } as Goal;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching goal:', error);
      return null;
    }
  }

  /**
   * Create or update a goal
   */
  async saveGoal(uid: string, goal: Goal): Promise<string> {
    try {
      const goalId = goal.id || `${goal.type}_${goal.frequency}`;
      const goalData = {
        type: goal.type,
        frequency: goal.frequency,
        um: goal.um,
        umShort: goal.umShort,
        isActive: goal.isActive,
        targetValue: goal.targetValue,
        dateUpdated: Date.now(),
        createdAt: goal.createdAt || Date.now(),
      };

      await setData(`goals/private/${uid}/${goalId}`, goalData);
      
      // Initialize current period stats if goal is active
      if (goal.isActive) {
        const period = this.getCurrentPeriod(goal.frequency);
        await this.initializeGoalStats(uid, goalId, goal.frequency, period, goal.type);
      }

      // Trigger goal created notification for new goals
      if (!goal.id) {
        goalNotificationsService.triggerGoalCreatedNotification({
          ...goalData,
          id: goalId,
        } as Goal);
      }

      return goalId;
    } catch (error) {
      logger.error('Error saving goal:', error);
      throw error;
    }
  }

  /**
   * Toggle goal active state
   */
  async toggleGoalActive(uid: string, goalId: string): Promise<void> {
    try {
      const goal = await this.getUserGoal(uid, goalId);
      if (goal) {
        await updateData(`goals/private/${uid}/${goalId}`, {
          isActive: !goal.isActive,
          dateUpdated: Date.now(),
        });

        // Initialize stats if activating
        if (!goal.isActive) {
          const period = this.getCurrentPeriod(goal.frequency);
          await this.initializeGoalStats(uid, goalId, goal.frequency, period, goal.type);
        }
      }
    } catch (error) {
      logger.error('Error toggling goal active state:', error);
    }
  }

  /**
   * Delete a goal
   */
  async deleteGoal(uid: string, goalId: string): Promise<void> {
    try {
      await removeData(`goals/private/${uid}/${goalId}`);
    } catch (error) {
      logger.error('Error deleting goal:', error);
    }
  }

  /**
   * Get goal statistics for a period
   */
  async getGoalStats(
    uid: string,
    frequency: GoalFrequency,
    period: string,
    type: GoalType
  ): Promise<GoalStats | null> {
    try {
      const statsData = await getData(`stats/${uid}/goals/${frequency}/${period}/${type}`);
      return statsData as GoalStats || null;
    } catch (error) {
      logger.error('Error fetching goal stats:', error);
      return null;
    }
  }

  /**
   * Initialize goal stats for a period
   */
  private async initializeGoalStats(
    uid: string,
    goalId: string,
    frequency: GoalFrequency,
    period: string,
    type: GoalType
  ): Promise<void> {
    try {
      const existingStats = await this.getGoalStats(uid, frequency, period, type);
      if (!existingStats) {
        await setData(`stats/${uid}/goals/${frequency}/${period}/${type}`, {
          progress: 0,
          achieved: false,
          lastTraining: null,
        });
      }
    } catch (error) {
      logger.error('Error initializing goal stats:', error);
    }
  }

  /**
   * Update goal progress from training
   */
  async updateGoalProgressFromTraining(
    uid: string,
    trainingDuration: number, // in milliseconds
    trainingDate: number
  ): Promise<void> {
    try {
      // Get user's active goals
      const goals = await this.getUserGoals(uid);
      const activeGoals = goals.filter(g => g.isActive);

      for (const goal of activeGoals) {
        const period = this.getCurrentPeriod(goal.frequency);
        const statsPath = `stats/${uid}/goals/${goal.frequency}/${period}/${goal.type}`;
        
        const currentStats = await this.getGoalStats(uid, goal.frequency, period, goal.type) || {
          progress: 0,
          achieved: false,
          lastTraining: null,
        };

        let newProgress = currentStats.progress;

        // Update progress based on goal type
        if (goal.type === 'time') {
          // Convert milliseconds to hours for time goals
          const hoursToAdd = trainingDuration / (1000 * 60 * 60);
          newProgress += hoursToAdd;
        } else if (goal.type === 'consistency') {
          // Increment by 1 for consistency goals
          newProgress += 1;
        }
        // Weight goals are updated separately

        const achieved = newProgress >= goal.targetValue;

        await setData(statsPath, {
          progress: newProgress,
          achieved,
          lastTraining: trainingDate,
        });

        // Check for milestone notifications
        goalNotificationsService.checkGoalMilestone(
          goal,
          newProgress,
          currentStats.progress
        );

        // Log achievement if newly achieved
        if (achieved && !currentStats.achieved) {
          logger.info(`Goal achieved: ${goal.id} for user ${uid}`);
          // Goal completion is handled by the 100% milestone
        }
      }
    } catch (error) {
      logger.error('Error updating goal progress:', error);
    }
  }

  /**
   * Update weight goal progress
   */
  async updateWeightGoalProgress(uid: string, newWeight: number): Promise<void> {
    try {
      const goals = await this.getUserGoals(uid);
      const weightGoals = goals.filter(g => g.isActive && g.type === 'weight');

      for (const goal of weightGoals) {
        const period = this.getCurrentPeriod(goal.frequency);
        const statsPath = `stats/${uid}/goals/${goal.frequency}/${period}/${goal.type}`;

        const currentStats = await this.getGoalStats(uid, goal.frequency, period, goal.type) || {
          progress: goal.targetValue, // Default to target for weight goals
          achieved: false,
          lastTraining: null,
        };

        const achieved = Math.abs(newWeight - goal.targetValue) < 0.5; // Within 0.5 kg of target

        await setData(statsPath, {
          progress: newWeight,
          achieved,
          lastUpdate: Date.now(),
        });

        // For weight goals, check if getting closer to target
        if (currentStats.progress !== newWeight) {
          const previousDistance = Math.abs(currentStats.progress - goal.targetValue);
          const currentDistance = Math.abs(newWeight - goal.targetValue);
          
          // If getting closer to target, check milestones
          if (currentDistance < previousDistance) {
            // Calculate progress as inverse of distance (closer = higher progress)
            const maxDistance = Math.abs(currentStats.progress - goal.targetValue);
            const progressPercentage = maxDistance > 0 ? ((maxDistance - currentDistance) / maxDistance) * 100 : 0;
            const previousPercentage = 0; // Starting from 0 for simplicity
            
            goalNotificationsService.checkGoalMilestone(
              { ...goal, targetValue: 100 }, // Treat as percentage
              progressPercentage,
              previousPercentage
            );
          }
        }
      }
    } catch (error) {
      logger.error('Error updating weight goal progress:', error);
    }
  }

  /**
   * Get goal progress for current period
   */
  async getCurrentGoalProgress(uid: string): Promise<GoalProgress[]> {
    try {
      const goals = await this.getUserGoals(uid);
      const progressList: GoalProgress[] = [];

      for (const goal of goals) {
        if (!goal.isActive || !goal.id) continue;

        const period = this.getCurrentPeriod(goal.frequency);
        const stats = await this.getGoalStats(uid, goal.frequency, period, goal.type);

        progressList.push({
          goalId: goal.id,
          period,
          currentValue: stats?.progress || 0,
          targetValue: goal.targetValue,
          lastUpdated: stats?.lastTraining || Date.now(),
          achieved: stats?.achieved || false,
        });
      }

      return progressList;
    } catch (error) {
      logger.error('Error fetching goal progress:', error);
      return [];
    }
  }

  /**
   * Subscribe to user's goals
   */
  subscribeToUserGoals(uid: string, callback: (goals: Goal[]) => void): () => void {
    const goalsRef = ref(database, `goals/private/${uid}`);
    
    const handleUpdate = (snapshot: any) => {
      if (snapshot.exists()) {
        const goalsData = snapshot.val();
        const goals = Object.entries(goalsData).map(([id, data]) => ({
          id,
          ...(data as Omit<Goal, 'id'>),
        }));
        callback(goals);
      } else {
        callback([]);
      }
    };

    onValue(goalsRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(goalsRef, 'value', handleUpdate);
    };
  }

  /**
   * Subscribe to goal stats
   */
  subscribeToGoalStats(
    uid: string,
    frequency: GoalFrequency,
    period: string,
    type: GoalType,
    callback: (stats: GoalStats | null) => void
  ): () => void {
    const statsRef = ref(database, `stats/${uid}/goals/${frequency}/${period}/${type}`);
    
    const handleUpdate = (snapshot: any) => {
      if (snapshot.exists()) {
        callback(snapshot.val() as GoalStats);
      } else {
        callback(null);
      }
    };

    onValue(statsRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(statsRef, 'value', handleUpdate);
    };
  }
}

export const goalsService = new GoalsService();
export default goalsService;